//#region IMPORTS
import { PhotoIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { useTranslation } from "react-i18next";
//#endregion

//#region INTERFACE
interface Props {
  // WoodWing thumbnail link; undefined while unknown or when there is none.
  src?: string;
  alt: string;
  onOpen: () => void;
}
//#endregion

export default function WeekOrderItemThumbnail({ src, alt, onOpen }: Props) {
  //#region SETUP
  const { t } = useTranslation();
  // Remember *which* link failed, so a fresh link (after the cache expired)
  // gets a new chance instead of staying on the placeholder.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImage = !!src && src !== failedSrc;
  //#endregion

  //#region RENDER
  // Every tile has the same landscape box (the WoodWing images are mostly
  // 3:2) and the image covers it, so the column reads as one even strip. An
  // image with another ratio gets cropped a little; the preview shows it whole.
  if (!showImage)
    return (
      <div
        className="h-[5.5rem] w-32 shrink-0 flex items-center justify-center rounded-lg bg-gray-100"
        aria-hidden="true"
      >
        <PhotoIcon className="h-8 w-8 text-gray-300" />
      </div>
    );

  return (
    <button
      type="button"
      onClick={onOpen}
      title={t("rituals.showLargerImage", "Show larger image")}
      className="h-[5.5rem] w-32 shrink-0 overflow-hidden rounded-lg bg-gray-100 ring-1 ring-gray-200 transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600"
    >
      <img
        src={src}
        alt={alt}
        onError={() => setFailedSrc(src ?? null)}
        className="h-full w-full object-cover"
      />
    </button>
  );
  //#endregion
}
